'use client';

import { FC, ReactNode, cloneElement, isValidElement, useState } from 'react';
import { Container, StyledButton, StyledImage } from './styled';
import { Box, Typography } from '@mui/material';
import { COLORS } from '@/constants';

import { useImageObserver } from '@/hooks';
import { useTranslations } from 'next-intl';
import { SendRequestModal } from '../SendRequestModal';

type QuickRequestModalProps = {
  objectId: string;
  whatsappDisabled: boolean;
  hasInfoRequest: boolean;
  brokerData: {
    imageFullUrl: string;
    name?: string | null;
    officeName?: string | null;
  };
  trigger?: ReactNode;
  hideButton?: boolean;
};

export const QuickRequestModal: FC<QuickRequestModalProps> = ({
  objectId,
  whatsappDisabled,
  brokerData,
  hasInfoRequest,
  trigger,
  hideButton = false,
}) => {
  const t = useTranslations();

  const { isBroken, imgSrc, handleError, handleLoad } = useImageObserver(brokerData?.imageFullUrl);

  const [isOpen, setIsOpen] = useState(false);

  const handleOpenModal = () => setIsOpen(true);
  const handleCloseModal = () => {
    setIsOpen(false);
  };

  return (
    <>
      {trigger ? (
        isValidElement(trigger) ? (
          cloneElement(trigger as React.ReactElement<{ onClick?: () => void }>, {
            onClick: handleOpenModal,
          })
        ) : (
          <span onClick={handleOpenModal}>{trigger}</span>
        )
      ) : (
        <Container>
          <Box display={'flex'} gap={'8px'} alignItems={'center'}>
            {imgSrc && !isBroken && (
              <StyledImage
                src={imgSrc}
                alt={`${brokerData?.name}-profile-image`}
                width={50}
                height={50}
                onError={handleError}
                onLoad={handleLoad}
              />
            )}

            <Box display={'flex'} gap={'4px'} flexDirection={'column'}>
              <Typography
                fontSize={12}
                fontWeight={700}
                lineHeight={'125%'}
                sx={{
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              >
                {brokerData?.name}
              </Typography>
              <Typography
                fontSize={12}
                fontWeight={500}
                lineHeight={'125%'}
                color={COLORS['neutral/600']}
                sx={{
                  display: '-webkit-box',
                  WebkitLineClamp: 1,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              >
                {brokerData?.officeName}
              </Typography>
            </Box>
          </Box>
          {!hideButton && (
            <StyledButton variant="secondary" onClick={handleOpenModal}>
              <Typography fontSize={12} lineHeight={'9px'} fontWeight={600}>
                {t('quickRequest')}
              </Typography>
            </StyledButton>
          )}
        </Container>
      )}

      <SendRequestModal
        open={isOpen}
        objectId={objectId}
        whatsappDisabled={whatsappDisabled}
        hasInfoRequest={hasInfoRequest}
        onClose={handleCloseModal}
        onComplete={handleCloseModal}
      />
    </>
  );
};
